import {
  getAgentTemplate,
  updateAgentTemplate,
  type AgentTemplate,
} from "@/api/agents";
import { captureDesktopChatAuthScopeGuard } from "@/desktop/desktop-chat-auth-scope";

type ConfigUpdate = Pick<
  Parameters<typeof updateAgentTemplate>[1],
  "adapter_type" | "adapter_config"
>;
const pendingUpdates = new Map<string, Promise<unknown>>();

/** Persist model/effort changes in order, even when their picker is remounted. */
export function updateAgentTemplateConfig(
  templateId: string,
  change: (current: AgentTemplate) => ConfigUpdate,
): Promise<AgentTemplate> {
  const isCurrentAuthScope = captureDesktopChatAuthScopeGuard();
  const previous = pendingUpdates.get(templateId) ?? Promise.resolve();
  const update = previous
    .catch(() => undefined)
    .then(async () => {
      if (!isCurrentAuthScope())
        throw new Error("Account changed before saving agent settings.");
      // Read after the previous write, rather than merging a render's stale config.
      const current = await getAgentTemplate(templateId);
      if (!isCurrentAuthScope())
        throw new Error("Account changed before saving agent settings.");
      return updateAgentTemplate(templateId, change(current));
    });
  pendingUpdates.set(templateId, update);
  const clear = () => {
    if (pendingUpdates.get(templateId) === update)
      pendingUpdates.delete(templateId);
  };
  void update.then(clear, clear);
  return update;
}
